import { cn } from '@/lib/core/utils/cn';
import { BookingDetailInfo, InfoItem } from './info';
import { Image } from '@/lib/core/utils';
import { useClientTranslation } from '@/lib/core';

export type { InfoItem };

export interface BookingDetailBannerProps {
  image?: Image | null;
  /**
   * Shown in place of `logo` when set. A venue booking is identified by its venue logo; an event
   * booking has no such mark — its venue is already on the summary's `WHERE` row, so the hero
   * carries the event's name instead, in the same face the event detail page titles it with.
   */
  title?: string | null;
  logo?: Image | null;
  rows?: InfoItem[][];
  adjustmentsLabel?: string | null;
  className?: string;
  style?: React.CSSProperties;
}

export function BookingDetailBanner({
  image,
  title,
  logo,
  rows = [],
  adjustmentsLabel,
  className,
  style,
}: BookingDetailBannerProps) {
  const translation = useClientTranslation();

  return (
    <div
      className={cn('relative flex w-full flex-col items-center justify-end gap-8 px-4 pb-10', className)}
      style={{ minHeight: 'calc(100vw * (580 / 402))', ...style }}
    >
      {image ? (
        <img
          src={image.src}
          alt={image.alt ?? ''}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="bg-jncquoi-neutral-900 pointer-events-none absolute inset-0" />
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(0,0,0,0.5) 3.64%, rgba(0,0,0,0.05) 21.79%, rgba(0,0,0,0.05) 49.62%, rgb(0,0,0) 83.59%)',
        }}
      />
      <div className="relative flex w-full flex-col items-center gap-6">
        {title ? (
          <h1 className="font-didot-lt-std text-jncquoi-neutral-00 text-center text-[40px] leading-[40px] font-black tracking-[-1.6px]">
            {title}
          </h1>
        ) : (
          logo && (
            <img
              src={logo.src}
              alt={logo.alt ?? translation('App.Labels.VenueLogo')}
              className="pointer-events-none size-56 object-contain"
            />
          )
        )}
        <BookingDetailInfo rows={rows} />
        {adjustmentsLabel && (
          <p className="font-pp-neue-montreal text-center text-[12px] tracking-[0.5px] text-white uppercase">
            {adjustmentsLabel}
          </p>
        )}
      </div>
    </div>
  );
}
